/**
 * Shared TypeScript contract for the documentclass-first kickoff.
 *
 * Owned by Team 5. Consumed by Team 3 (DocumentStamp popover) and
 * Team 4 (NewDocumentDialog). The C# DTO `CreateDocumentDto` in
 * `lilia-editor-api` mirrors this shape 1:1.
 *
 * Once committed against, fields are append-only — renames or removals
 * require a coordinated PR across all five teams.
 *
 * Reference: lilia-docs/teams/2026-05-06-documentclass-first/01-shared-contract.md
 */

// ---------------------------------------------------------------------------
// Categories and classes
// ---------------------------------------------------------------------------

/**
 * The four document categories shipped in round 1.
 * Letter and Slides are reserved tile slots, deferred.
 */
export type DocumentCategory = "article" | "book" | "report" | "cv";

/**
 * The actual LaTeX class strings. Each category has a default class.
 * Round 1 surfaces only the canonical class per category; alternates
 * (e.g. amsart, IEEEtran, tufte-book, memoir, altacv) are reserved
 * for the within-category "Change" overlay once they ship.
 */
export type DocumentClass =
  | "article"
  | "amsart"
  | "IEEEtran"
  | "acmart"
  | "exam"
  | "book"
  | "tufte-book"
  | "report"
  | "memoir"
  | "moderncv"
  | "altacv"
  | "resume";

/**
 * Default class per category. Used when the user picks a category
 * in the create dialog or stamp popover without overriding the class.
 */
export const DEFAULT_CLASS_FOR_CATEGORY: Record<DocumentCategory, DocumentClass> = {
  article: "article",
  book: "book",
  report: "report",
  cv: "moderncv",
};

// ---------------------------------------------------------------------------
// Class options
// ---------------------------------------------------------------------------

/** The universal class options (B2 / E3 grid). */
export interface ClassOptionsValue {
  paperSize: "a4" | "letter" | "a5";
  fontSize: 10 | 11 | 12;
  columns: 1 | 2;
  /** Shown only when columns === 2. Off by default. */
  balancedColumns: boolean;
  sides: "one" | "two";
  /** false = inline (\maketitle), true = titlepage environment */
  titlePage: boolean;
  orientation: "portrait" | "landscape";
}

/** Per-category defaults for ClassOptionsValue. From B2's mapping. */
export const DEFAULT_OPTIONS_FOR_CATEGORY: Record<DocumentCategory, ClassOptionsValue> = {
  article: {
    paperSize: "a4",
    fontSize: 11,
    columns: 1,
    balancedColumns: false,
    sides: "one",
    titlePage: false,
    orientation: "portrait",
  },
  book: {
    paperSize: "a4",
    fontSize: 11,
    columns: 1,
    balancedColumns: false,
    sides: "two",
    titlePage: true,
    orientation: "portrait",
  },
  report: {
    paperSize: "a4",
    fontSize: 11,
    columns: 1,
    balancedColumns: false,
    sides: "two",
    titlePage: true,
    orientation: "portrait",
  },
  cv: {
    paperSize: "a4",
    fontSize: 10,
    columns: 1,
    balancedColumns: false,
    sides: "one",
    titlePage: false,
    orientation: "portrait",
  },
};

// ---------------------------------------------------------------------------
// Component props
// ---------------------------------------------------------------------------

/** CategoryTilePicker props. */
export interface CategoryTilePickerProps {
  value: DocumentCategory;
  onChange: (next: DocumentCategory) => void;
  /**
   * Show the inline "Change" link below the tile grid. False in B1
   * (the dialog itself is the picker). True in E2 (popover).
   */
  showChangeLink?: boolean;
  /** Click handler for the "Change" link. Required when showChangeLink. */
  onChangeClassClick?: () => void;
  /** Within-category alternates list. Used by the Change overlay. */
  currentClass?: DocumentClass;
}

/** ClassOptionsGrid props. */
export interface ClassOptionsGridProps {
  value: ClassOptionsValue;
  onChange: (next: ClassOptionsValue) => void;
  /**
   * Hide specific options. Beamer hides everything except font size +
   * (later) page size. Only set when surfacing in slides context.
   */
  hidden?: Array<keyof ClassOptionsValue>;
}

// ---------------------------------------------------------------------------
// Server contract
// ---------------------------------------------------------------------------

/**
 * The DTO shape Team 4 sends from the create dialog.
 * MUST match Team 2's CreateDocumentDto in C# (1:1 fields).
 */
export interface CreateDocumentRequest {
  title: string;
  language: string;
  documentCategory: DocumentCategory;
  documentClass: DocumentClass;
  // class-options fields, flattened to match the server DTO shape:
  paperSize: ClassOptionsValue["paperSize"];
  fontSize: ClassOptionsValue["fontSize"];
  columns: ClassOptionsValue["columns"];
  balancedColumns: boolean;
  sides: "one" | "two";
  titlePage: boolean;
  orientation: "portrait" | "landscape";
  // optional:
  templateId?: string;
}
